/**
 * Live supervisor wallboard.
 *
 * Every figure comes from the server's own computation over persisted state;
 * nothing here is derived from a hardcoded number. Readable at 1366x768 and up
 * to a wall-mounted display.
 */

import { clsx } from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useSession } from '@/app/session';
import { ApiError, api } from '@/lib/api/client';
import type { Wallboard as WallboardData } from '@/lib/api/types';
import { formatDuration, formatTime } from '@/lib/format';
import { connectRealtime } from '@/lib/realtime';
import type { ConnectionStatus } from '@/lib/realtime';
import { Badge, Button, EmptyState, ErrorState, LoadingState, Metric, Panel, STATE_TONE } from '@/ui/primitives';

export function Wallboard() {
  const { can } = useSession();
  const [board, setBoard] = useState<WallboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus>('connecting');
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setBoard(await api.get<WallboardData>('/supervisor/wallboard'));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Unable to load the wallboard');
    }
  }, []);

  useEffect(() => {
    void refresh();
    const disconnect = connectRealtime(['wallboard'], {
      onMessage: () => void refresh(),
      onStatus: setConnection,
      onResync: () => void refresh(),
    });
    const timer = setInterval(() => void refresh(), 5000);
    return () => {
      disconnect();
      clearInterval(timer);
    };
  }, [refresh]);

  const supervise = async (callId: string, mode: 'monitor' | 'whisper' | 'barge') => {
    setBusy(`${callId}:${mode}`);
    try {
      await api.post(`/supervisor/calls/${callId}/supervise`, { mode });
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Supervision failed');
    } finally {
      setBusy(null);
    }
  };

  if (error && !board) return <ErrorState message={error} onRetry={() => void refresh()} />;
  if (!board) return <LoadingState label="Loading wallboard" />;

  const available = board.agents.filter((agent) => agent.state === 'available').length;
  const onCall = board.agents.filter((agent) => agent.state === 'on_call' || agent.state === 'hold').length;
  const inAcw = board.agents.filter((agent) => agent.state === 'acw').length;
  const waiting = board.queues.reduce((total, queue) => total + queue.waiting, 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-px bg-line">
      <div className="flex shrink-0 items-center bg-surface">
        <Metric label="Live calls" value={board.live_calls} />
        <Metric label="Waiting" value={waiting} tone={waiting > 0 ? 'warn' : 'neutral'} />
        <Metric label="Available" value={available} tone="ok" />
        <Metric label="On call" value={onCall} />
        <Metric label="Wrap-up" value={inAcw} />
        <Metric label="Agents" value={board.agents.length} />
        <div className="ml-auto flex items-center gap-3 px-3 text-2xs uppercase tracking-wide text-faint">
          <span>Updated {formatTime(board.generated_at)}</span>
          <span
            className={clsx(
              'inline-flex items-center gap-1',
              connection === 'online' ? 'text-ok' : connection === 'offline' ? 'text-danger' : 'text-warn',
            )}
          >
            <span
              className={clsx(
                'h-1.5 w-1.5 rounded-full',
                connection === 'online' ? 'bg-ok' : connection === 'offline' ? 'bg-danger' : 'bg-warn',
              )}
            />
            {connection}
          </span>
        </div>
      </div>

      {board.alerts.length ? (
        <div className="flex shrink-0 flex-wrap gap-2 border-b border-warn/40 bg-warn-soft px-3 py-1.5">
          {board.alerts.map((alert) => (
            <span key={`${alert.type}-${alert.agent_id}`} className="text-xs text-warn">
              {alert.message}
            </span>
          ))}
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_280px] gap-px bg-line">
        <Panel title="Agents">
          {board.agents.length === 0 ? (
            <EmptyState message="No agents configured" />
          ) : (
            <table className="w-full border-collapse text-base">
              <thead className="sticky top-0 bg-surface-2 text-left text-2xs uppercase tracking-wide text-faint">
                <tr>
                  <th className="h-7 px-2 font-medium">Agent</th>
                  <th className="h-7 px-2 font-medium">State</th>
                  <th className="h-7 px-2 font-medium">In state</th>
                  <th className="h-7 px-2 font-medium">Customer</th>
                  <th className="h-7 px-2 font-medium">Call</th>
                  <th className="h-7 px-2 font-medium">Hold</th>
                  <th className="h-7 px-2 font-medium">Handled</th>
                  <th className="h-7 px-2 font-medium">AHT</th>
                  <th className="h-7 px-2 font-medium">Audio</th>
                  <th className="h-7 px-2 font-medium">Intervene</th>
                </tr>
              </thead>
              <tbody>
                {board.agents.map((agent) => (
                  <tr
                    key={agent.user_id}
                    className={clsx(
                      'h-row border-b border-line/60',
                      agent.acw_overrun && 'bg-danger-soft',
                    )}
                  >
                    <td className="px-2">
                      <span className="truncate">{agent.full_name}</span>
                      {agent.extension ? (
                        <span className="knx-num ml-1 text-xs text-faint">{agent.extension}</span>
                      ) : null}
                    </td>
                    <td className="px-2">
                      <Badge tone={STATE_TONE[agent.state] ?? 'neutral'}>
                        {agent.state.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="knx-num px-2 text-muted">{formatDuration(agent.seconds_in_state)}</td>
                    <td className="knx-num px-2">{agent.customer ?? '—'}</td>
                    <td className="knx-num px-2">{agent.current_call_id ? formatDuration(agent.call_seconds) : '—'}</td>
                    <td className="knx-num px-2">{formatDuration(agent.hold_seconds)}</td>
                    <td className="knx-num px-2">{agent.calls_handled}</td>
                    <td className="knx-num px-2">{formatDuration(agent.aht_seconds)}</td>
                    <td className="knx-num px-2">{agent.audio_quality_mos.toFixed(1)}</td>
                    <td className="px-2">
                      <div className="flex gap-1">
                        {(['monitor', 'whisper', 'barge'] as const).map((mode) => {
                          const capability = `supervisor:${mode}`;
                          const allowed = can(capability) && Boolean(agent.current_call_id);
                          return (
                            <Button
                              key={mode}
                              size="sm"
                              variant="ghost"
                              disabled={!allowed || busy === `${agent.current_call_id}:${mode}`}
                              title={
                                !can(capability)
                                  ? `Your role does not include ${capability}`
                                  : !agent.current_call_id
                                    ? 'The agent is not on a call'
                                    : `${mode} this call`
                              }
                              onClick={() =>
                                agent.current_call_id && supervise(agent.current_call_id, mode)
                              }
                            >
                              {mode}
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Queues">
          {board.queues.length === 0 ? (
            <EmptyState message="No queues configured" />
          ) : (
            board.queues.map((queue) => (
              <div key={queue.queue_id} className="border-b border-line/60 px-2 py-1.5">
                <div className="flex items-center justify-between">
                  <span className="truncate">{queue.name}</span>
                  <span className={clsx('knx-num', queue.waiting > 0 ? 'text-warn' : 'text-muted')}>
                    {queue.waiting}
                  </span>
                </div>
                <div className="flex justify-between text-2xs text-faint">
                  <span>Longest wait {formatDuration(queue.longest_wait_seconds)}</span>
                  <span>
                    SL {queue.service_level_target_pct}% / {queue.service_level_seconds}s
                  </span>
                </div>
              </div>
            ))
          )}
        </Panel>
      </div>
    </div>
  );
}
